import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Icon, SelectMenu, SimpleModal } from '@livable/saas-react-components';
import { Button, MenuItem, TextField } from '@mui/material';
import Papa from 'papaparse';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import useModalFooterStyles from 'src/styles/cutomModalFooter';
import useStyles from './styles';

/** The most a CSV column may hold before the picker stops being a picker. */
const MAX_OPTIONS = 2000;
const EMPTY_MAPPING = {
  id: '',
  name: ''
};

/**
 * How a PMS unit reads while being chosen: the name Ops recognise, with the id that does the
 * matching beside it. The id stays in the option even though the row shows it in its own column,
 * because it is what makes two units of the same name tellable apart.
 */
const labelFor = _ref => {
  let id = _ref.id,
    name = _ref.name;
  return name ? "".concat(name, " \xB7 ").concat(id) : id;
};

/** How it reads once chosen, with the id already in the next column. */
const pickedLabelFor = _ref2 => {
  let id = _ref2.id,
    name = _ref2.name;
  return name || id;
};

/**
 * Maps Livable units to the units their PMS knows, by name and by id.
 *
 * The PM uploads any CSV their PMS exports, says which columns hold the unit id and the unit name,
 * and then picks a unit per row — the flow agreed with QA. Ops match by name, because an id like
 * 12873 says nothing about which unit it belongs to, so the picker is labelled by name and
 * searched by either. The id is what the import matches on, so it is the id a row is keyed by and
 * the id the counter counts.
 *
 * A file with no name column works the same way, labelled by id alone. Typing straight in is still
 * allowed on the unit row, because a PM with three units will not go and fetch a file for them.
 *
 * Nothing is saved from here: the picks go back to the unit list, and the page's Save writes them.
 */
export const PmsUnitMappingModal = _ref3 => {
  let isOpen = _ref3.isOpen,
    onClose = _ref3.onClose,
    units = _ref3.units,
    onApply = _ref3.onApply;
  const classes = useStyles();
  const footerClasses = useModalFooterStyles({
    modalHasNoMargin: true,
    modalHasNoPadding: true
  });
  const fileInputRef = useRef(null);
  const _useState = useState({}),
    _useState2 = _slicedToArray(_useState, 2),
    mappings = _useState2[0],
    setMappings = _useState2[1];
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    fileName = _useState4[0],
    setFileName = _useState4[1];
  const _useState5 = useState([]),
    _useState6 = _slicedToArray(_useState5, 2),
    headers = _useState6[0],
    setHeaders = _useState6[1];
  const _useState7 = useState([]),
    _useState8 = _slicedToArray(_useState7, 2),
    rows = _useState8[0],
    setRows = _useState8[1];
  const _useState9 = useState(''),
    _useState0 = _slicedToArray(_useState9, 2),
    idColumn = _useState0[0],
    setIdColumn = _useState0[1];
  const _useState1 = useState(''),
    _useState10 = _slicedToArray(_useState1, 2),
    nameColumn = _useState10[0],
    setNameColumn = _useState10[1];
  const _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    isDragging = _useState12[0],
    setIsDragging = _useState12[1];
  const _useState13 = useState(''),
    _useState14 = _slicedToArray(_useState13, 2),
    parseError = _useState14[0],
    setParseError = _useState14[1];
  const _useState15 = useState(''),
    _useState16 = _slicedToArray(_useState15, 2),
    columnSearch = _useState16[0],
    setColumnSearch = _useState16[1];
  const _useState17 = useState(''),
    _useState18 = _slicedToArray(_useState17, 2),
    rowSearch = _useState18[0],
    setRowSearch = _useState18[1];
  const _useState19 = useState(null),
    _useState20 = _slicedToArray(_useState19, 2),
    openRow = _useState20[0],
    setOpenRow = _useState20[1];

  // Opening starts from what the units already carry, so the modal shows the current state
  // rather than an empty grid the PM would have to fill in again.
  useEffect(() => {
    if (!isOpen) return;
    setMappings(Object.fromEntries(units.map((unit, index) => {
      var _unit$pmsUnitId, _unit$pmsUnitName;
      return [index, {
        id: (_unit$pmsUnitId = unit.pmsUnitId) !== null && _unit$pmsUnitId !== void 0 ? _unit$pmsUnitId : '',
        name: (_unit$pmsUnitName = unit.pmsUnitName) !== null && _unit$pmsUnitName !== void 0 ? _unit$pmsUnitName : ''
      }];
    })));
    setFileName('');
    setHeaders([]);
    setRows([]);
    setIdColumn('');
    setNameColumn('');
    setParseError('');
    setColumnSearch('');
    setRowSearch('');
    setOpenRow(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // One entry per PMS unit, keyed by id because that is what has to be unique: two units may well
  // share a name, and the id beside it is what tells them apart.
  const options = useMemo(() => {
    if (!idColumn) return [];
    const byId = new Map();
    rows.forEach(row => {
      var _row$idColumn, _row$nameColumn;
      const id = ((_row$idColumn = row[idColumn]) !== null && _row$idColumn !== void 0 ? _row$idColumn : '').trim();
      if (!id || byId.has(id)) return;
      byId.set(id, {
        id,
        name: nameColumn ? ((_row$nameColumn = row[nameColumn]) !== null && _row$nameColumn !== void 0 ? _row$nameColumn : '').trim() : ''
      });
    });
    return [...byId.values()].sort((a, b) => labelFor(a).localeCompare(labelFor(b), undefined, {
      numeric: true
    })).slice(0, MAX_OPTIONS);
  }, [rows, idColumn, nameColumn]);

  // How many units carry each id. One-to-one is the rule, so anything above one is a clash, and
  // counting says so for every row involved rather than for whichever one happens to be last.
  const idCounts = useMemo(() => {
    const counts = new Map();
    Object.values(mappings).forEach(mapping => {
      var _mapping$id, _counts$get;
      const id = ((_mapping$id = mapping === null || mapping === void 0 ? void 0 : mapping.id) !== null && _mapping$id !== void 0 ? _mapping$id : '').trim();
      if (id) counts.set(id, ((_counts$get = counts.get(id)) !== null && _counts$get !== void 0 ? _counts$get : 0) + 1);
    });
    return counts;
  }, [mappings]);

  // Counted by id, not by name: a unit with only a name is still unmapped, because the import has
  // nothing to match on.
  const mappedCount = Object.values(mappings).filter(mapping => {
    var _mapping$id2;
    return ((_mapping$id2 = mapping === null || mapping === void 0 ? void 0 : mapping.id) !== null && _mapping$id2 !== void 0 ? _mapping$id2 : '').trim();
  }).length;
  const parseFile = file => {
    setParseError('');
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      // Trimmed here rather than after the fact: Papa keys every row by the header exactly as the
      // file spells it, so trimming only the picker's copy left the lookup asking for a key no row
      // had, and a file whose header carried a space offered nothing to choose from.
      transformHeader: header => header.trim(),
      complete: result => {
        var _result$meta$fields, _result$meta, _result$data;
        const parsedHeaders = ((_result$meta$fields = (_result$meta = result.meta) === null || _result$meta === void 0 ? void 0 : _result$meta.fields) !== null && _result$meta$fields !== void 0 ? _result$meta$fields : []).filter(Boolean);
        if (parsedHeaders.length === 0) {
          setParseError('That file has no header row to pick a column from.');
          setHeaders([]);
          setRows([]);
          return;
        }
        setHeaders(parsedHeaders);
        setRows((_result$data = result.data) !== null && _result$data !== void 0 ? _result$data : []);
        setIdColumn('');
        setNameColumn('');
      },
      error: () => setParseError('That file could not be read as a CSV.')
    });
  };
  const handleFile = file => {
    if (!file) return;
    setFileName(file.name);
    parseFile(file);
  };
  const clearFile = () => {
    setFileName('');
    setHeaders([]);
    setRows([]);
    setIdColumn('');
    setNameColumn('');
    setParseError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const setMapping = (index, mapping) => setMappings(prev => _objectSpread(_objectSpread({}, prev), {}, {
    [index]: mapping
  }));
  const matches = (text, term) => text.toLowerCase().includes(term.trim().toLowerCase());

  /** Searched by either, because Ops come with a name and support come with an id. */
  const optionMatches = (option, term) => matches(option.id, term) || matches(option.name, term);
  const handleApply = () => {
    onApply(mappings);
    onClose();
  };
  const columnPicker = (id, label, value, onPick) => /*#__PURE__*/React.createElement("div", {
    className: classes.columnRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.columnLabel
  }, label), /*#__PURE__*/React.createElement("div", {
    className: classes.columnSelect
  }, /*#__PURE__*/React.createElement(SelectMenu, {
    id: id,
    labelId: "".concat(id, "-label"),
    displayEmpty: true,
    enableSearch: true,
    searchPlaceholder: "Search columns",
    onSearch: setColumnSearch,
    isOpen: openRow === id,
    handleOpen: () => {
      setColumnSearch('');
      setOpenRow(id);
    },
    handleClose: () => {
      setColumnSearch('');
      setOpenRow(null);
    },
    value: value,
    onChange: event => {
      onPick(event.target.value);
      setColumnSearch('');
    },
    renderValue: picked => picked || /*#__PURE__*/React.createElement("span", {
      className: classes.placeholder
    }, "Select a column")
  }, /*#__PURE__*/React.createElement(MenuItem, {
    value: ""
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.placeholder
  }, "None")), headers.filter(header => matches(header, columnSearch)).map(header => /*#__PURE__*/React.createElement(MenuItem, {
    key: header,
    value: header
  }, header)))));
  return /*#__PURE__*/React.createElement(SimpleModal, {
    handleClose: onClose,
    isOpen: isOpen,
    noFooter: true,
    title: "Map PMS units",
    subTitle: "Give each Livable unit the name and id your PMS knows it by, so the charge export needs no hand-correcting",
    variant: "large"
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.dropzone, " ").concat(isDragging ? classes.dropzoneActive : '', " ").concat(fileName ? classes.dropzoneFilled : ''),
    onClick: () => {
      var _fileInputRef$current;
      return !fileName && ((_fileInputRef$current = fileInputRef.current) === null || _fileInputRef$current === void 0 ? void 0 : _fileInputRef$current.click());
    },
    onDragOver: event => {
      event.preventDefault();
      setIsDragging(true);
    },
    onDragLeave: () => setIsDragging(false),
    onDrop: event => {
      var _event$dataTransfer$f;
      event.preventDefault();
      setIsDragging(false);
      handleFile((_event$dataTransfer$f = event.dataTransfer.files) === null || _event$dataTransfer$f === void 0 ? void 0 : _event$dataTransfer$f[0]);
    }
  }, fileName ? /*#__PURE__*/React.createElement("div", {
    className: classes.fileRow
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "file-text"
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.fileName
  }, fileName), /*#__PURE__*/React.createElement(Button, {
    variant: "actionIcon",
    "aria-label": "remove file",
    onClick: event => {
      event.stopPropagation();
      clearFile();
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "trash"
  }))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Icon, {
    type: "cloud-upload"
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.dropzoneHint
  }, "Drop a CSV from your PMS here, or click to choose one. Optional \u2014 you can type the ids in below.")), /*#__PURE__*/React.createElement("input", {
    ref: fileInputRef,
    type: "file",
    accept: ".csv,text/csv",
    hidden: true,
    onChange: event => {
      var _event$target$files;
      return handleFile((_event$target$files = event.target.files) === null || _event$target$files === void 0 ? void 0 : _event$target$files[0]);
    }
  })), parseError ? /*#__PURE__*/React.createElement("div", {
    className: classes.error
  }, parseError) : null, headers.length > 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, columnPicker('pms-id-column-selector', 'Column holding the unit id', idColumn, setIdColumn), columnPicker('pms-name-column-selector', 'Column holding the unit name', nameColumn, setNameColumn), idColumn ? /*#__PURE__*/React.createElement("span", {
    className: classes.columnHint
  }, options.length === 1 ? '1 value' : "".concat(options.length, " values"), " found") : null) : null, /*#__PURE__*/React.createElement("div", {
    className: classes.listHeader
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.listCount
  }, mappedCount, " of ", units.length, " mapped")), /*#__PURE__*/React.createElement("div", {
    className: classes.table
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.tableHead
  }, /*#__PURE__*/React.createElement("span", null, "Livable unit"), /*#__PURE__*/React.createElement("span", null, "PMS unit name"), /*#__PURE__*/React.createElement("span", null, "PMS unit id")), units.map((unit, index) => {
    var _mappings$index, _mapping$id3, _idCounts$get, _mapping$name, _mapping$id4;
    const mapping = (_mappings$index = mappings[index]) !== null && _mappings$index !== void 0 ? _mappings$index : EMPTY_MAPPING;
    const id = ((_mapping$id3 = mapping.id) !== null && _mapping$id3 !== void 0 ? _mapping$id3 : '').trim();
    const isDuplicate = Boolean(id) && ((_idCounts$get = idCounts.get(id)) !== null && _idCounts$get !== void 0 ? _idCounts$get : 0) > 1;
    // A unit's current mapping stays on offer even when the file has no such id. Dropping it
    // left the row reading "Not mapped" over an id that was still there and that Apply would
    // still hand back — the screen and the button disagreeing about the same unit.
    const rowOptions = id && !options.some(option => option.id === id) ? [mapping, ...options] : options;
    return /*#__PURE__*/React.createElement("div", {
      className: classes.tableRow,
      key: "".concat(unit.id, "-").concat(index)
    }, /*#__PURE__*/React.createElement("span", {
      className: classes.unitName
    }, unit.name), options.length > 0 ?
    /*#__PURE__*/
    /* Searchable, and the reason the picker exists at all: a PMS export routinely
       carries hundreds of units, which is far past what anyone will scroll. */
    React.createElement(SelectMenu, {
      id: "pms-unit-selector-".concat(index),
      labelId: "pms-unit-selector-".concat(index, "-label"),
      displayEmpty: true,
      error: isDuplicate,
      enableSearch: true,
      searchPlaceholder: "Search by name or id",
      onSearch: setRowSearch,
      isOpen: openRow === index,
      handleOpen: () => {
        setRowSearch('');
        setOpenRow(index);
      },
      handleClose: () => {
        setRowSearch('');
        setOpenRow(null);
      },
      value: id,
      onChange: event => {
        const picked = rowOptions.find(option => option.id === event.target.value);

        // Both halves come off the same CSV row, so a name can never end up beside an
        // id it did not come with.
        setMapping(index, picked ? {
          id: picked.id,
          name: picked.name
        } : EMPTY_MAPPING);
        setRowSearch('');
      },
      renderValue: () => id ? pickedLabelFor(mapping) : /*#__PURE__*/React.createElement("span", {
        className: classes.placeholder
      }, "Not mapped")
    }, /*#__PURE__*/React.createElement(MenuItem, {
      value: ""
    }, /*#__PURE__*/React.createElement("span", {
      className: classes.placeholder
    }, "Not mapped")), rowOptions
    // One PMS unit belongs to one Livable unit, so an id already spoken for is not
    // offered again — except to the row already holding it. The row's own value is
    // always rendered, searched or not, so the select never holds a value its menu
    // does not contain.
    .filter(option => option.id === id || !idCounts.has(option.id) && optionMatches(option, rowSearch)).map(option => /*#__PURE__*/React.createElement(MenuItem, {
      key: option.id,
      value: option.id
    }, labelFor(option)))) : /*#__PURE__*/React.createElement(TextField, {
      size: "small",
      fullWidth: true,
      placeholder: "PMS unit name",
      value: (_mapping$name = mapping.name) !== null && _mapping$name !== void 0 ? _mapping$name : '',
      onChange: event => setMapping(index, _objectSpread(_objectSpread({}, mapping), {}, {
        name: event.target.value
      }))
    }), options.length > 0 ? /*#__PURE__*/React.createElement("span", {
      className: "".concat(classes.pickedId, " ").concat(id ? '' : classes.placeholder)
    }, id || '—') : /*#__PURE__*/React.createElement(TextField, {
      size: "small",
      fullWidth: true,
      placeholder: "Not mapped",
      value: (_mapping$id4 = mapping.id) !== null && _mapping$id4 !== void 0 ? _mapping$id4 : '',
      error: isDuplicate,
      onChange: event => setMapping(index, _objectSpread(_objectSpread({}, mapping), {}, {
        id: event.target.value
      }))
    }));
  })), /*#__PURE__*/React.createElement("div", {
    className: "".concat(footerClasses.modalFooter, " ").concat(footerClasses.footerEven)
  }, /*#__PURE__*/React.createElement("div", {
    className: footerClasses.footerWrapper
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "text",
    onClick: onClose
  }, "Cancel")), /*#__PURE__*/React.createElement("div", {
    className: footerClasses.footerWrapper
  }, /*#__PURE__*/React.createElement("div", {
    className: footerClasses.footerButton
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    onClick: handleApply
  }, "Apply")))));
};
export default PmsUnitMappingModal;